import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Folder, Clock, Archive, Trash2, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import TESTIDS from '../constants/testIds';
import SelfDevPanel from '../components/SelfDevPanel';
import { useT } from '../contexts/LocaleContext';
import { CreditsButton } from '../components/credits/CreditsButton';

interface Workspace {
  id: string;
  name: string;
  description?: string;
  status: string;
  last_activity: string;
  created_at: string;
}

const Dashboard = () => {
  const t = useT();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newWorkspace, setNewWorkspace] = useState({ name: '', description: '' });
  const [confirmDelete, setConfirmDelete] = useState<Workspace | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showSelfDev, setShowSelfDev] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadWorkspaces();
    // Determine admin visibility purely on the client — server still
    // enforces admin on every /self/* endpoint. We just gate the UI.
    apiService.getMe().then((me: { email?: string }) => {
      setIsAdmin(!!me?.email && me.email.startsWith('admin@'));
    }).catch(() => setIsAdmin(false));
  }, []);

  const loadWorkspaces = async () => {
    try {
      const data = await apiService.listWorkspaces();
      setWorkspaces(data);
    } catch (error) {
      toast.error(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      const workspace = await apiService.createWorkspace(newWorkspace);
      toast.success(t('dashboard.createSuccess'));
      setWorkspaces([workspace, ...workspaces]);
      setIsDialogOpen(false);
      setNewWorkspace({ name: '', description: '' });
      // Redirect straight into the workspace so the user can start
      // describing the app without an extra click. This matches the
      // demo flow: "Nuevo Workspace → escribe el prompt".
      if (workspace?.id) {
        navigate(`/workspace/${workspace.id}`);
      }
    } catch (error) {
      toast.error(t('dashboard.createError'));
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return t('dashboard.timeLessThanHour');
    if (hours < 24) return t(hours > 1 ? 'dashboard.timeHours' : 'dashboard.timeHour', { n: hours });
    if (days < 7) return t(days > 1 ? 'dashboard.timeDays' : 'dashboard.timeDay', { n: days });
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#050505] p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white mb-2">
              {t('dashboard.title')}
            </h1>
            <p className="text-[#A1A1AA] text-base">
              {t('dashboard.subtitle')}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <CreditsButton testId="dashboard-credits-btn" />
            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                data-testid="dashboard-admin-btn"
                className="bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/20 font-mono uppercase text-xs rounded-md px-3 py-2"
              >
                Admin
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setShowSelfDev((v) => !v)}
                data-testid="dashboard-self-dev-toggle"
                className="bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 font-mono uppercase text-xs rounded-md px-3 py-2"
              >
                {showSelfDev ? 'Ocultar auto-dev' : 'Auto-desarrollo'}
              </button>
            )}
            <button
              onClick={() => setIsDialogOpen(true)}
              data-testid={TESTIDS.DASHBOARD_CREATE_WORKSPACE}
              className="bg-white text-black font-medium hover:bg-neutral-200 transition-colors shadow-[0_0_15px_rgba(255,255,255,0.2)] rounded-md px-4 py-2 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.newWorkspace')}
            </button>
          </div>
        </div>

        {isAdmin && showSelfDev && (
          <div className="mb-8 p-4 border border-white/10 rounded-lg bg-white/[0.02]">
            <SelfDevPanel />
          </div>
        )}

        {/* Create Dialog */}
        {isDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setIsDialogOpen(false)}>
            <div className="bg-[#0E0F11] border border-white/10 rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-2xl font-medium tracking-tight text-white mb-4">{t('dashboard.createDialogTitle')}</h3>
              <form onSubmit={handleCreateWorkspace} className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">
                    {t('auth.name')}
                  </label>
                  <input
                    type="text"
                    value={newWorkspace.name}
                    onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                    placeholder={t('dashboard.workspaceNamePlaceholder')}
                    required
                    data-testid={TESTIDS.WORKSPACE_NAME_INPUT}
                    className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">
                    {t('dashboard.descriptionOptional')}
                  </label>
                  <textarea
                    value={newWorkspace.description}
                    onChange={(e) => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                    placeholder={t('dashboard.descriptionPlaceholder')}
                    data-testid={TESTIDS.WORKSPACE_DESCRIPTION_INPUT}
                    rows={3}
                    className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all rounded-md px-3 py-2 resize-none"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={creating}
                    data-testid={TESTIDS.WORKSPACE_SUBMIT}
                    className="flex-1 bg-white text-black font-medium hover:bg-neutral-200 transition-colors rounded-md py-3"
                  >
                    {creating ? t('dashboard.creating') : t('dashboard.createDialogTitle')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsDialogOpen(false)}
                    className="px-6 bg-transparent border border-white/10 text-white hover:bg-white/5 transition-colors rounded-md"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Workspaces Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" style={{ width: '40px', height: '40px' }} />
          </div>
        ) : workspaces.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative h-96 flex items-center justify-center rounded-xl overflow-hidden border border-white/5"
          >
            <div className="empty-state-bg absolute inset-0" />
            <div className="relative z-10 text-center">
              <Folder className="w-16 h-16 text-[#52525B] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[#A1A1AA] text-lg mb-2">{t('dashboard.emptyTitle')}</p>
              <p className="text-[#52525B] text-sm">{t('dashboard.emptyHint')}</p>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {workspaces.map((workspace, index) => (
              <motion.div
                key={workspace.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div
                  data-testid={TESTIDS.WORKSPACE_CARD}
                  onClick={() => navigate(`/workspace/${workspace.id}`)}
                  className="bg-[#0E0F11] border border-white/5 p-6 rounded-xl cursor-pointer card-hover h-full"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <Folder className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    {workspace.status === 'active' && (
                      <span className="status-active px-2 py-0.5 rounded-full text-xs font-mono">
                        ACTIVE
                      </span>
                    )}
                    {workspace.status === 'archived' && (
                      <span className="flex items-center text-[#52525B] text-xs font-mono">
                        <Archive className="w-3 h-3 mr-1" />
                        ARCHIVED
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-medium tracking-tight text-white mb-2">
                    {workspace.name}
                  </h3>
                  {workspace.description && (
                    <p className="text-[#A1A1AA] text-sm mb-4 line-clamp-2">
                      {workspace.description}
                    </p>
                  )}
                  <div className="flex items-center text-xs text-[#52525B] font-mono mt-auto pt-4 border-t border-white/5">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatDate(workspace.last_activity)}
                    <button
                      type="button"
                      data-testid={`workspace-delete-${workspace.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(workspace);
                      }}
                      className="ml-auto p-1.5 -m-1 rounded hover:bg-[#FF3B30]/10 text-[#52525B] hover:text-[#FF3B30] transition-colors"
                      title={t('dashboard.delete')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => !deleting && setConfirmDelete(null)}
        >
          <div
            className="bg-[#0E0F11] border border-white/10 rounded-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="workspace-delete-modal"
          >
            <div className="flex items-center mb-3">
              <div className="p-2 bg-[#FF3B30]/10 rounded-md mr-3">
                <Trash2 className="w-5 h-5 text-[#FF3B30]" />
              </div>
              <h3 className="text-xl text-white font-medium">{t('dashboard.delete')}</h3>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-2">
              {t('dashboard.deletePrefix')} <span className="text-white font-mono">{confirmDelete.name}</span> {t('dashboard.deleteSuffix')}
            </p>
            <ul className="text-xs text-[#52525B] mb-5 space-y-0.5 ml-1">
              <li>• {t('dashboard.deleteMissions')}</li>
              <li>• {t('dashboard.deleteMemories')}</li>
              <li>• {t('dashboard.deleteIndex')}</li>
              <li>• {t('dashboard.deleteSandbox')}</li>
            </ul>
            <p className="text-xs text-[#FF3B30] mb-5">{t('dashboard.deleteWarning')}</p>
            <div className="flex gap-3">
              <button
                type="button"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await apiService.deleteWorkspace(confirmDelete.id);
                    setWorkspaces((ws) => ws.filter((w) => w.id !== confirmDelete.id));
                    toast.success(t('dashboard.deleteSuccess'));
                    setConfirmDelete(null);
                  } catch {
                    toast.error(t('dashboard.deleteError'));
                  } finally {
                    setDeleting(false);
                  }
                }}
                data-testid="workspace-delete-confirm"
                className="flex-1 bg-[#FF3B30] text-white font-medium rounded-md py-2.5 hover:bg-[#FF3B30]/90 disabled:opacity-60 transition-colors flex items-center justify-center"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {deleting ? t('dashboard.deleting') : t('dashboard.deleteConfirmBtn')}
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => setConfirmDelete(null)}
                data-testid="workspace-delete-cancel"
                className="px-6 bg-transparent border border-white/10 text-white rounded-md hover:bg-white/5 transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
